import { useState } from "react";
import Footer from "../../components/feature/Footer";

const rules = [
  {
    id: 1,
    section: "General Conduct",
    tone: "from-emerald-500/20 to-cyan-500/10",
    icon: "ri-shield-check-line",
    summary: "Keep chat respectful, readable, and welcoming.",
    items: [
      "Respect all players and staff at all times. Harassment, bullying, or hate speech of any kind will result in an immediate ban.",
      "No racism, sexism, homophobia, or any other form of discrimination. This is a zero-tolerance policy.",
      "Do not spam chat, commands, or private messages. Keep conversations relevant and civil.",
      "Impersonating staff members or other players is strictly prohibited.",
      "English is the primary language in global chat. Use language channels for other languages.",
    ],
  },
  {
    id: 2,
    section: "Gameplay Rules",
    tone: "from-red-500/20 to-orange-500/10",
    icon: "ri-gamepad-line",
    summary: "Play fair. No hacked clients, unfair mods, or exploit abuse.",
    items: [
      "Hacking, cheating, or using unauthorized mods is permanently bannable. This includes X-ray, kill aura, fly hacks, and speed hacks.",
      "Exploiting game bugs or glitches for personal gain is not allowed. Report exploits to staff immediately.",
    ],
  },
  {
    id: 3,
    section: "Economy & Trading",
    tone: "from-amber-500/20 to-lime-500/10",
    icon: "ri-exchange-line",
    summary: "Trades should be honest and store purchases must stay legitimate.",
    items: [
      "Scamming other players in trades is strictly prohibited and will result in punishment.",
      "Do not attempt to manipulate the in-game economy through price fixing or market cornering.",
      "Real-money trades for in-game items or currency are banned, except through the official store.",
      "Chargebacks on store purchases will result in a permanent ban from all services.",
    ],
  },
  {
    id: 4,
    section: "Communication",
    tone: "from-sky-500/20 to-violet-500/10",
    icon: "ri-chat-3-line",
    summary: "Keep public chat safe, server-focused, and appropriate.",
    items: [
      "Do not share personal information about yourself or other players.",
      "Advertising other Minecraft servers in chat is prohibited.",
      "Keep content family-friendly in global chat. Explicit or NSFW content is not allowed.",
      "Do not use the report system to falsely report players. Abuse of the report system is punishable.",
    ],
  },
  {
    id: 5,
    section: "Staff & Appeals",
    tone: "from-fuchsia-500/20 to-rose-500/10",
    icon: "ri-customer-service-2-line",
    summary: "Use the proper appeal process instead of arguing in chat.",
    items: [
      "Staff decisions are final. Arguing with staff publicly in chat is not allowed.",
      "If you feel a punishment was unfair, submit an appeal through our Discord.",
      "Do not attempt to contact staff in-game to appeal a ban. Use the official appeal process.",
      "False reports or appeals made in bad faith will result in additional penalties.",
    ],
  },
];

export default function RulesPage() {
  const [openSection, setOpenSection] = useState(1);
  const totalRules = rules.reduce((sum, section) => sum + section.items.length, 0);
  const selectedSection = rules.find((section) => section.id === openSection) ?? rules[0];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <main className="flex-1 w-full">
        <section className="border-b border-border/60 bg-[radial-gradient(circle_at_top_left,rgba(255,77,94,0.18),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.035),transparent)]">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 py-12 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
              <div>
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
                  <i className="ri-file-list-3-line" />
                  AppleSMP Rules
                </div>
                <h1 className="max-w-3xl text-4xl font-black tracking-tight text-foreground sm:text-5xl">
                  Clear rules for a fair survival server.
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
                  These rules keep AppleSMP safe, readable, and competitive. Staff may act based on severity, context, and player history.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3 rounded-2xl border border-border bg-card/80 p-3 shadow-2xl shadow-black/20">
                <div className="rounded-xl bg-background/70 p-4 text-center">
                  <p className="text-2xl font-black text-primary">{rules.length}</p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Sections</p>
                </div>
                <div className="rounded-xl bg-background/70 p-4 text-center">
                  <p className="text-2xl font-black text-primary">{totalRules}</p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Rules</p>
                </div>
                <div className="rounded-xl bg-background/70 p-4 text-center">
                  <p className="text-2xl font-black text-primary">0</p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Tolerance</p>
                </div>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/10 p-4">
                <i className="ri-alert-line text-lg text-yellow-400" />
                <p className="mt-2 text-sm font-semibold">Breaking rules can lead to warnings, mutes, temporary bans, or permanent bans.</p>
              </div>
              <div className="rounded-xl border border-sky-500/20 bg-sky-500/10 p-4">
                <i className="ri-discord-line text-lg text-sky-300" />
                <p className="mt-2 text-sm font-semibold">Appeals and questions should go through Discord so staff can review them properly.</p>
              </div>
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4">
                <i className="ri-heart-2-line text-lg text-emerald-300" />
                <p className="mt-2 text-sm font-semibold">Use common sense. If something gives an unfair advantage, report it.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto grid w-full max-w-6xl gap-6 px-4 py-10 sm:px-6 lg:grid-cols-[280px_1fr] lg:px-8">
          <aside className="h-fit rounded-2xl border border-border bg-card p-3 lg:sticky lg:top-24">
            <p className="px-3 pb-2 pt-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Rule Sections</p>
            <div className="flex flex-col gap-1">
              {rules.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setOpenSection(section.id)}
                  className={`flex items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors ${
                    openSection === section.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                >
                  <i className={`${section.icon} text-base`} />
                  <span className="text-sm font-bold">{section.section}</span>
                </button>
              ))}
            </div>
          </aside>

          <div className="flex flex-col gap-4">
            <article className="overflow-hidden rounded-2xl border border-primary/30 bg-card shadow-2xl shadow-black/20">
              <div className="flex items-center gap-4 border-b border-border/50 p-5">
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${selectedSection.tone}`}>
                  <i className={`${selectedSection.icon} text-xl text-foreground`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-black text-foreground">{selectedSection.section}</h2>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
                      {selectedSection.items.length} rules
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{selectedSection.summary}</p>
                </div>
              </div>

              <div className="bg-background/35 p-5">
                <ol className="grid gap-3">
                  {selectedSection.items.map((rule, idx) => (
                    <li key={rule} className="flex gap-3 rounded-xl border border-border/60 bg-card/80 p-4">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-black text-primary-foreground">
                        {idx + 1}
                      </span>
                      <p className="text-sm leading-6 text-muted-foreground">{rule}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </article>

            <div className="rounded-2xl border border-border bg-card p-6 text-center">
              <i className="ri-question-line mb-3 block text-3xl text-primary" />
              <h2 className="text-lg font-black">Need clarification?</h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">
                Ask a staff member before doing something questionable. It is easier to ask first than appeal later.
              </p>
              <a
                href="https://discord.gg/applesmp"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <i className="ri-discord-line" /> Join Discord
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
